import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
  output,
  TemplateRef,
  viewChildren,
} from '@angular/core';
import { MatIcon } from '@angular/material/icon';

import {
  ImageBehavior,
  makeFontStyleStyles,
  OptionsIconSettings,
  OptionsImageSettings,
  PropertiesConfigInputFontStyleOptions,
  TileSelectorModes,
} from '@blaze-shared/elements-domain';
import { DataRowFieldValuePipe, FormatValuePipe } from '@blaze-shared/shared-feature';
import {
  ImgErrorModule,
  mapOfTemplates,
  MarkdownPipe,
  SecurityTrustModule,
} from '@blaze-shared/shared-ui';
import { toRatio } from '@blaze-shared/shared-util';

import { CardContentConfig } from '../../elements/checkbox/checkbox.model';

interface CardStylesConfig {
  heading?: {
    align?: string;
  };
  subheading?: {
    color?: string;
    fontWeight?: string;
    fontSize?: string;
    fontStyle?: PropertiesConfigInputFontStyleOptions;
    spaceAbove?: string;
    align?: string;
  };
  description?: {
    color?: string;
    fontSize?: string;
    fontWeight?: string;
    spaceAbove?: string;
    align?: string;
  };
  customField?: {
    color?: string;
    fontSize?: string;
    spaceAbove?: string;
    align?: string;
  };
}

@Component({
  selector: 'blaze-selectable-tile',
  imports: [
    CommonModule,
    ImgErrorModule,
    MatIcon,
    DataRowFieldValuePipe,
    FormatValuePipe,
    SecurityTrustModule,
    MarkdownPipe,
  ],
  templateUrl: './selectable-tile.component.html',
  styleUrl: './selectable-tile.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SelectableTileComponent {
  mode = input<TileSelectorModes>('image');
  label = input<string | undefined>('');
  subLabel = input<string | undefined>();
  subLabelStyles = input<Record<string, string> | undefined>();
  imageSrc = input<string | undefined>();
  showImage = input<boolean>(false);
  icon = input<string | undefined>();
  showIcon = input<boolean>(true);
  labelPosition = input<string>('top');
  labelStyles = input<Record<string, string> | null>();
  tileAlign = input<string | undefined>();
  iconSettings = input<OptionsIconSettings | undefined>();
  imageSettings = input<OptionsImageSettings | undefined>();
  showCheckmark = input<boolean>(false);
  selected = input<boolean>(false);
  isReadOnly = input<boolean>(false);
  cardContent = input<CardContentConfig | undefined>();
  cardStyles = input<CardStylesConfig | undefined>();

  dataCardAction = output<{ action: 'copy' | 'openPage'; value?: unknown }>();

  tpls = viewChildren(TemplateRef<unknown>);

  protected readonly subheadingStyles = computed(() => {
    const styles = this.cardStyles()?.subheading;
    if (!styles) {
      return {};
    }
    const fontStyleStyles = makeFontStyleStyles(styles.fontStyle) ?? {};
    return {
      color: styles.color,
      fontWeight: styles.fontWeight,
      fontSize: styles.fontSize,
      paddingTop: styles.spaceAbove,
      textAlign: styles.align ?? 'left',
      ...fontStyleStyles,
    };
  });

  protected readonly cardHeadingStyles = computed(() => {
    return {
      ...this.tileStyle(),
      textAlign: this.cardStyles()?.heading?.align ?? 'left',
    };
  });

  protected readonly descriptionStyles = computed(() => {
    const styles = this.cardStyles()?.description;
    if (!styles) {
      return {};
    }
    return {
      color: styles.color,
      fontSize: styles.fontSize,
      fontWeight: styles.fontWeight,
      paddingTop: styles.spaceAbove,
      textAlign: styles.align ?? 'left',
    };
  });

  protected readonly customFieldStyles = computed(() => {
    const styles = this.cardStyles()?.customField;
    if (!styles) {
      return {};
    }
    return {
      color: styles.color,
      fontSize: styles.fontSize,
    };
  });

  protected readonly customFieldsSectionStyles = computed(() => {
    const customField = this.cardStyles()?.customField;
    return {
      paddingTop: customField?.spaceAbove ?? '16px',
      textAlign: customField?.align ?? 'left',
    };
  });

  protected readonly customFieldsFlexAlign = computed(() => {
    const align = this.cardStyles()?.customField?.align ?? 'left';
    if (align === 'center') {
      return 'center';
    } else if (align === 'right') {
      return 'flex-end';
    } else {
      return 'flex-start';
    }
  });

  protected readonly cardFontFamily = computed(() => {
    return { fontFamily: this.labelStyles()?.fontFamily };
  });

  protected imageWidth = computed(() => {
    const imgSettings = this.imageSettings();
    if (!imgSettings) {
      return 100;
    }
    return parseDimension(imgSettings.width) ?? 100;
  });

  protected imageHeight = computed(() => {
    const imgSettings = this.imageSettings();
    if (!imgSettings) {
      return 100;
    }
    return parseDimension(imgSettings.height) ?? 100;
  });

  protected imageRatio = computed(() => {
    const imgSettings = this.imageSettings();
    if (!imgSettings) {
      return 'none';
    }
    return imgSettings.ratio;
  });

  protected imageBehavior = computed(() => {
    const imgSettings = this.imageSettings();
    if (!imgSettings) {
      return 'cover' as ImageBehavior;
    }
    return imgSettings.behavior;
  });

  protected readonly finalHeight = computed(() => {
    if (this.imageRatio() === 'none') {
      return this.imageHeight();
    }
    const { width, height } = toRatio(this.imageRatio());
    const factor = height / width;
    return this.imageWidth() * factor;
  });

  readonly tileStyle = computed(() => {
    let justifyContent: string;
    switch (this.tileAlign()) {
      case 'center':
        justifyContent = 'center';
        break;
      case 'right':
        justifyContent = 'flex-end';
        break;
      default:
        justifyContent = 'flex-start';
    }
    const ngStyle = this.labelStyles();
    return {
      justifyContent: justifyContent,
      ...(ngStyle ?? {}),
    };
  });

  readonly boxTileContentStyles = computed(() => {
    switch (this.tileAlign()) {
      case 'center':
        return { textAlign: 'center' };
      case 'right':
        return { textAlign: 'right' };
      default:
        return { textAlign: 'left' };
    }
  });

  imageStyles = computed(() => {
    const width = this.imageWidth();

    if (this.imageRatio() === 'circle') {
      return {
        height: `${width}px`,
        width: `${width}px`,
        objectFit: this.imageBehavior(),
        borderRadius: '50%',
      };
    }

    return {
      height: `${this.finalHeight()}px`,
      width: `${width}px`,
      objectFit: this.imageBehavior(),
      borderRadius: '4px',
    };
  });

  imagePlaceholderStyles = computed(() => {
    const width = this.imageWidth();

    const finalHeight = this.finalHeight();
    const smallerDimension = Math.min(width ?? Infinity, finalHeight ?? Infinity);
    const padding = Math.min(12, smallerDimension * 0.15);
    const imageSize = Math.max(0, smallerDimension - padding * 2);
    const commonStyles = {
      '--icon-size': imageSize + 'px',
      padding: `${padding}px`,
    };

    if (this.imageRatio() === 'circle') {
      return {
        ...commonStyles,
        borderRadius: '50%',
        height: `${width}px`,
        width: `${width}px`,
      };
    }

    return {
      ...commonStyles,
      borderRadius: '4px',
      height: `${this.finalHeight()}px`,
      width: `${width}px`,
    };
  });

  iconSize = computed(() => {
    const iconSettings = this.iconSettings();
    if (!iconSettings?.size) {
      return '16px';
    }
    return parseDimension(iconSettings.size) + 'px';
  });

  checkIconBehavior = computed(() => {
    return Object.entries({
      'absolute left-8 top-1/2 transform -translate-y-1/2':
        this.labelPosition() === 'right' || this.tileAlign() === 'right',
      'absolute right-8 top-1/2 transform -translate-y-1/2':
        (this.labelPosition() === 'top' ||
          this.labelPosition() === 'bottom' ||
          this.labelPosition() === 'left') &&
        this.tileAlign() !== 'right',
    })
      .filter(([_, condition]) => condition)
      .map(([className, _]) => className)
      .join(' ');
  });

  mainIconBehavior = computed(() => {
    if (this.labelPosition() === 'right') {
      return 'absolute right-8 top-1/2 transform -translate-y-1/2';
    }
    return '';
  });

  cellsMap = computed(() => {
    return mapOfTemplates([...this.tpls()], 'CellTpl');
  });

  columnTypeClicked(columnType: string, value?: string): void {
    switch (columnType) {
      case 'url':
        return this.dataCardAction.emit({ action: 'openPage', value });
      case 'email':
        return this.dataCardAction.emit({ action: 'copy', value });
      default:
        return;
    }
  }
}

const parseDimension = (raw: string | number | undefined): number | undefined => {
  return typeof raw === 'string' ? parseInt(raw.replace('px', ''), 10) : raw;
};
